import {
	BadgeOutlined,
	EventAvailableOutlined,
	FactCheckOutlined,
	PendingActionsOutlined,
} from "@mui/icons-material";
import { Stack } from "@mui/material";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import colors from "../../../config/colors";
import KpiCard from "../../../design/kpi-card";
import {
	EnabledFeatures,
	SupervisorOverviewSnapshot,
} from "../store/supervisor-home-types";
import { HOME_LINKS } from "../utils/queue-navigation";

interface SnapshotKpisProps {
	snapshot?: SupervisorOverviewSnapshot;
	enabledFeatures?: EnabledFeatures;
	isLoading: boolean;
}

function SnapshotKpis({
	snapshot,
	enabledFeatures,
	isLoading,
}: SnapshotKpisProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleClick = useCallback(
		(url: string) => {
			navigate(url);
		},
		[navigate]
	);

	const loading = isLoading ? "pending" : "success";
	const shiftsToday = snapshot?.shiftsToday;
	const ftShifts = snapshot?.ftShiftsToday;
	const requests = snapshot?.requestsPending;
	const evaluations = snapshot?.evaluations;

	// `enabledFeatures` is unknown until the call lands. Assume on so the cards
	// don't flash in and out; a disabled feature only ever removes a card.
	const showFtShifts = enabledFeatures?.fullTimeScheduling !== false;
	const showEvaluations = enabledFeatures?.skillEvaluationForm !== false;
	const showTimeOff = enabledFeatures?.attendanceManagement !== false;
	const showShiftSwap = enabledFeatures?.shiftSwap !== false;

	const requestsCount =
		(showTimeOff ? requests?.timeOff ?? 0 : 0) +
		(showShiftSwap ? requests?.shiftSwap ?? 0 : 0);

	const requestsSubTitle = [
		showTimeOff
			? t("supervisor-home_kpi-time-off-count", {
				count: requests?.timeOff ?? 0,
			})
			: null,
		showShiftSwap
			? t("supervisor-home_kpi-swap-count", { count: requests?.shiftSwap ?? 0 })
			: null,
	]
		.filter(Boolean)
		.join(" · ");

	const cards = [
		{
			key: "flexShifts",
			icon: <EventAvailableOutlined />,
			iconBackgroundColor: colors.selected,
			iconColor: colors.button,
			count: shiftsToday?.scheduled ?? 0,
			title: t("supervisor-home_kpi-flex-shifts-today"),
			subTitle: t("supervisor-home_kpi-flex-shifts-subtitle", {
				filled: shiftsToday?.filled ?? 0,
				open: shiftsToday?.open ?? 0,
			}),
			url: HOME_LINKS.flexSchedule,
		},
		showFtShifts
			? {
				key: "ftShifts",
				icon: <BadgeOutlined />,
				iconBackgroundColor: colors.noticeBox,
				iconColor: colors.noticeIcon,
				count: ftShifts?.shiftsScheduled ?? 0,
				title: t("supervisor-home_kpi-ft-shifts-today"),
				subTitle: t("supervisor-home_kpi-ft-shifts-subtitle", {
					calledOff: ftShifts?.calledOff ?? 0,
					training: ftShifts?.training ?? 0,
				}),
				url: HOME_LINKS.fullTimeSchedule,
			}
			: null,
		{
			key: "requests",
			icon: <PendingActionsOutlined />,
			iconBackgroundColor: colors.adminInfoBackgroundColor,
			iconColor: colors.adminInfoIconColor,
			count: requestsCount,
			title: t("supervisor-home_kpi-pending-requests"),
			subTitle: requestsSubTitle,
			url: showTimeOff
				? HOME_LINKS.timeOffRequests
				: HOME_LINKS.flexShiftRequests,
		},
		showEvaluations
			? {
				key: "evaluations",
				icon: <FactCheckOutlined />,
				iconBackgroundColor: colors.infoBoxBackground,
				iconColor: colors.warningColor,
				count: evaluations?.total ?? 0,
				title: t("supervisor-home_kpi-evaluations"),
				subTitle: t("supervisor-home_kpi-evaluations-subtitle", {
					toFill: evaluations?.toFill ?? 0,
					toAck: evaluations?.toAck ?? 0,
				}),
				url: HOME_LINKS.evaluations,
			}
			: null,
	].filter(Boolean) as {
		key: string;
		icon: React.ReactNode;
		iconBackgroundColor: string;
		iconColor: string;
		count: number;
		title: string;
		subTitle: string;
		url: string;
	}[];

	return (
		<Stack direction={{ xs: "column", sm: "row" }} sx={{ flexWrap: "wrap" }}>
			{cards.map((card) => (
				<KpiCard
					key={card.key}
					icon={card.icon}
					iconBackgroundColor={card.iconBackgroundColor}
					iconColor={card.iconColor}
					kpiDataCount={card.count}
					title={card.title}
					subTitle={card.subTitle}
					loading={loading}
					isToggled={false}
					handleClick={handleClick}
					onClickActionType={card.url}
					kpiData={card.url}
				/>
			))}
		</Stack>
	);
}

export default SnapshotKpis;
